export const TAX_MODES = Object.freeze({
  EXEMPT: 'exempt',
  VAT_5_5: '5.5',
  VAT_10: '10',
  VAT_20: '20',
});

export const LABOUR_MODES = Object.freeze({
  HOURS: 'hours',
  FIXED: 'fixed',
});

export const emptyMaterialLine = () => ({
  material_name: '',
  material_quantity: '',
  material_price: '',
});

export const parseDecimal = (value) => {
  if (typeof value === 'string') {
    const normalized = value.replace(/\s/g, '').replace(',', '.');
    if (!normalized) return 0;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

export const roundMoney = (value) => {
  const parsed = parseDecimal(value);
  return Number.isFinite(parsed) ? Math.round((parsed + Number.EPSILON) * 100) / 100 : 0;
};

const hasMeaningfulValue = (value) => String(value ?? '').trim() !== '';

export const taxModeFromContract = (contract = {}) => {
  const label = String(contract.tax_label || '').toLowerCase();
  if (label.includes('293 b') || label.includes('non applicable')) return TAX_MODES.EXEMPT;
  const rate = roundMoney(contract.vat_rate);
  if (rate === 5.5) return TAX_MODES.VAT_5_5;
  if (rate === 10) return TAX_MODES.VAT_10;
  if (rate === 20) return TAX_MODES.VAT_20;
  return rate > 0 ? String(rate) : '';
};

export const labourModeFromContract = (contract = {}) => {
  if (contract.labour_mode === LABOUR_MODES.HOURS || contract.labour_mode === LABOUR_MODES.FIXED) {
    return contract.labour_mode;
  }
  const hasHours = Number(contract.labour_hours) > 0 || Number(contract.hourly_rate) > 0;
  return hasHours ? LABOUR_MODES.HOURS : LABOUR_MODES.FIXED;
};

export const normalizeMaterialLines = (lines = []) => (Array.isArray(lines) ? lines : [])
  .map((line = {}) => ({
    material_name: String(line.material_name || '').trim().slice(0, 160),
    material_quantity: parseDecimal(line.material_quantity),
    material_price: parseDecimal(line.material_price),
  }))
  .filter((line) => (
    line.material_name
    || Number(line.material_quantity) !== 0
    || Number(line.material_price) !== 0
  ));

const resolveLabour = (input = {}, errors = []) => {
  const requestedMode = input.labourMode === LABOUR_MODES.HOURS
    ? LABOUR_MODES.HOURS
    : input.labourMode === LABOUR_MODES.FIXED
      ? LABOUR_MODES.FIXED
      : (hasMeaningfulValue(input.labourHours) || hasMeaningfulValue(input.hourlyRate)
        ? LABOUR_MODES.HOURS
        : LABOUR_MODES.FIXED);

  if (requestedMode === LABOUR_MODES.HOURS) {
    const labourHours = parseDecimal(input.labourHours);
    const hourlyRate = parseDecimal(input.hourlyRate);
    const hasHours = hasMeaningfulValue(input.labourHours);
    const hasRate = hasMeaningfulValue(input.hourlyRate);

    if (hasHours || hasRate) {
      if (!Number.isFinite(labourHours) || labourHours <= 0) errors.push('Le nombre d’heures de main-d’œuvre est invalide.');
      if (!hasRate || !Number.isFinite(hourlyRate) || hourlyRate <= 0) errors.push('Renseignez un taux horaire HT supérieur à zéro.');
    }

    const wage = Number.isFinite(labourHours) && labourHours > 0 && hasRate && Number.isFinite(hourlyRate) && hourlyRate > 0
      ? roundMoney(labourHours * hourlyRate)
      : 0;

    return {
      labourMode: LABOUR_MODES.HOURS,
      labourHours: Number.isFinite(labourHours) ? labourHours : 0,
      hourlyRate: Number.isFinite(hourlyRate) ? roundMoney(hourlyRate) : 0,
      wage,
    };
  }

  const fixedWage = parseDecimal(input.wage);
  if (!Number.isFinite(fixedWage) || fixedWage < 0) errors.push('Le montant forfaitaire de main-d’œuvre est invalide.');
  return {
    labourMode: LABOUR_MODES.FIXED,
    labourHours: 0,
    hourlyRate: 0,
    wage: roundMoney(fixedWage),
  };
};

export const validateQuoteInput = ({ materials = [], wage = 0, labourMode, labourHours, hourlyRate, travelCost = 0, taxMode = '' } = {}) => {
  const normalizedMaterials = normalizeMaterialLines(materials);
  const errors = [];

  normalizedMaterials.forEach((line, index) => {
    if (!line.material_name) errors.push(`Matériau ${index + 1} : désignation manquante.`);
    if (!Number.isFinite(line.material_quantity) || line.material_quantity <= 0) {
      errors.push(`Matériau ${index + 1} : quantité invalide.`);
    }
    if (!Number.isFinite(line.material_price) || line.material_price < 0) {
      errors.push(`Matériau ${index + 1} : prix unitaire invalide.`);
    }
  });

  const labour = resolveLabour({ wage, labourMode, labourHours, hourlyRate }, errors);
  const travel = parseDecimal(travelCost);
  if (!Number.isFinite(travel) || travel < 0) errors.push('Le montant de déplacement est invalide.');

  const rate = taxMode === TAX_MODES.EXEMPT ? 0 : parseDecimal(taxMode);
  if (!taxMode || !Number.isFinite(rate) || rate < 0 || rate > 100) {
    errors.push('Choisissez un régime de TVA valide.');
  }

  return {
    errors,
    normalizedMaterials,
    labour,
    travel,
    vatRate: rate,
  };
};

export const calculateQuote = (input = {}) => {
  const validation = validateQuoteInput(input);
  const materialCost = roundMoney(validation.normalizedMaterials.reduce(
    (sum, line) => sum + (line.material_quantity * line.material_price),
    0,
  ));
  const wage = roundMoney(validation.labour.wage);
  const travelCost = roundMoney(validation.travel);
  const subtotalHt = roundMoney(materialCost + wage + travelCost);
  const vatRate = roundMoney(validation.vatRate);
  const vatAmount = roundMoney(subtotalHt * vatRate / 100);
  const totalTtc = roundMoney(subtotalHt + vatAmount);
  const taxLabel = input.taxMode === TAX_MODES.EXEMPT
    ? 'TVA non applicable, art. 293 B du CGI'
    : `TVA ${String(vatRate).replace('.', ',')} %`;

  return {
    errors: validation.errors,
    materialList: validation.normalizedMaterials.map((line) => ({
      ...line,
      material_quantity: roundMoney(line.material_quantity),
      material_price: roundMoney(line.material_price),
      line_total_ht: roundMoney(line.material_quantity * line.material_price),
    })),
    materialCost,
    labourMode: validation.labour.labourMode,
    labourHours: validation.labour.labourHours,
    hourlyRate: validation.labour.hourlyRate,
    wage,
    travelCost,
    subtotalHt,
    vatRate,
    vatAmount,
    totalTtc,
    taxLabel,
    valid: validation.errors.length === 0 && totalTtc > 0,
  };
};
